// @ts-check

import cleanSource from '@/helpers/clean-source'
import { i18n } from '@/i18n'
import { parseRecursively } from '@spl-company-oy/core'
import camelCaseKeys from 'camelcase-keys'
import { format } from 'date-fns'
import * as _ from 'lodash-es'
import snakeCaseKeys from 'snakecase-keys'

export const omitNulls = (/** @type {object} */ obj) => {
  const clonedObject = _.cloneDeep(obj)
  parseRecursively(
    clonedObject,
    ({ value }) => value === null,
    () => undefined
  )
  return clonedObject
}

export function formatNumberToFinnishLocale(value) {
  if (typeof value !== 'number' && typeof value !== 'string') {
    return ''
  }

  const cleanValue = cleanSource(value)
  let number = cleanValue

  if (typeof value === 'string') {
    const convertedNumber = Number(value)

    if (Number.isNaN(convertedNumber)) {
      return ''
    }

    number = convertedNumber
  }

  const fixedFloat = number.toFixed(2)
  const [beforeComma, afterComma] = fixedFloat.toString().split('.')

  let formattedBeforeComma = ''
  const { length } = beforeComma

  for (let index = length - 1; index > -1; index -= 1) {
    formattedBeforeComma = beforeComma.charAt(index) + formattedBeforeComma
    const cnt = length - index // distance from the end of the string
    if (cnt % 3 === 0 && index !== 0 && beforeComma.charAt(index - 1) !== '-') {
      formattedBeforeComma = ` ${formattedBeforeComma}`
    }
  }

  return `${formattedBeforeComma},${afterComma}`
}

/**
 * Format a sum to Finnish currency format.
 * @param {number|string} value - Brief description of the parameter here. Note: For other notations of data types, please refer to JSDocs: DataTypes command.
 * @return {string} Value in Finnish currency format (two decimals, comma separator, euro sign in the end)
 */
export function formatToFinnishCurrency(value) {
  const formattedNumber = formatNumberToFinnishLocale(value)

  return formattedNumber === '' ? '' : `${formattedNumber} €`
}

export function formattedAccount(account) {
  return account.replace(/.{4}(?=.)/g, '$& ')
}

export const formattedPeriod = ({
  id,
  period_start: periodStart,
  period_end: periodEnd
}) => {
  const startDate = format(new Date(periodStart), 'dd.MM.yyyy')
  const endDate = format(new Date(periodEnd), 'dd.MM.yyyy')
  return {
    label: `${startDate} - ${endDate}`,
    value: id
  }
}

export const formattedDimension = ({ id, type }) => {
  return {
    label: i18n.global.t(`reporting_dimensions.${type}`),
    value: id
  }
}

export const formatBankAccountNumber = (bankAccount) => {
  const formatted = bankAccount.account_number?.replace(/.{4}(?=.)/g, '$& ')

  if (!bankAccount.is_default) {
    return formatted
  }

  return `${formatted} (${i18n.global.t('general.default')})`
}

export const formattedTag = ({ id, name }) => {
  return {
    label: name,
    value: id
  }
}

/**
 * Truncates the given text if its length is greater than the given
 * max characters.
 *
 * @param {string} text
 * @param {number} maxCharacters
 */
export const truncateText = (text, maxCharacters) =>
  text.length > maxCharacters ? `${text.slice(0, maxCharacters)}...` : text

/**
 * Converts the given object's keys recursively to snake-case.
 *
 * @template {Parameters<typeof snakeCaseKeys>[0]} T
 * @param {T} value
 *
 * @example<caption>Basic usage.</caption>
 *
 * const input = {
 *      camelCase: {
 *          innerCamelCase: 'valueCamelCase'
 *      }
 * };
 *
 * // Returns { camel_case: { inner_camel_case: 'valueCamelCase'}}
 * const output = toSnakeCase(input);
 */
export const toSnakeCase = (value) => snakeCaseKeys(value, { deep: true })

/**
 * Converts the given object's keys recursively to camel-case.
 *
 * @template {Parameters<typeof camelCaseKeys>[0]} T
 * @param {T} value
 *
 * @example<caption>Basic usage.</caption>
 *
 * const input = {
 *      snake_case: {
 *          inner_snake_case: 'value_snake_case'
 *      }
 * };
 *
 * // Returns { snakeCase: { innerSnakeCase: 'value_snake_case'}}
 * const output = toCamelCase(input);
 */
export const toCamelCase = (value) => camelCaseKeys(value, { deep: true })

/**
 * @param {number} fraction
 */
export const formatFraction = (fraction) => {
  return `${(fraction * 100).toFixed(2).replace('.', ',')} %`
}
